import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import NewTopicModal from './components/NewTopicModal';
import TopicDetailModal from './components/TopicDetailModal';
import { communityBoards, topics, type CommunityTopic } from '@/mocks/community';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Community() {
  usePageMeta(
    '社区交流 - 鲁啊鲁多元内容平台',
    '在鲁啊鲁社区参与话题讨论、分享见解，与更多读者交流互动，发现不同视角。',
  );
  const [activeBoard, setActiveBoard] = useState('hot');
  const [topicList, setTopicList] = useState<CommunityTopic[]>(topics);
  const [selectedTopic, setSelectedTopic] = useState<CommunityTopic | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2200);
  };

  const visibleTopics = useMemo(() => {
    if (activeBoard === 'following') {
      return topicList.filter((item) => item.board === 'following');
    }
    if (activeBoard === 'hot') {
      return topicList.filter((item) => item.board === 'hot');
    }
    return [...topicList].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }, [activeBoard, topicList]);

  const handleSubmit = (data: { title: string; board: string; content: string }) => {
    const newTopic: CommunityTopic = {
      id: `topic-local-${Date.now()}`,
      title: data.title,
      author: '我',
      avatar: '我',
      replies: 0,
      updatedAt: new Date().toLocaleString('zh-CN', { hour12: false }).replace(/\//g, '-'),
      board: data.board,
      tags: ['新话题'],
      content: [data.content],
    };
    setTopicList((prev) => [newTopic, ...prev]);
    setActiveBoard(data.board);
    setNewOpen(false);
    showToast('话题已发布（演示，仅本次会话可见）');
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8 pb-28 lg:pb-8">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '社区' }]} />

      <div className="mt-5 rounded-lg bg-primary-50 border border-primary-100 px-6 py-7 md:px-9 md:py-9">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">社区交流</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              在这里和志同道合的人聊聊最近的发现、困惑与灵感。每一个认真的表达，都值得被看见。
            </p>
          </div>
          <button
            type="button"
            onClick={() => setNewOpen(true)}
            className="hidden md:inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors self-start md:self-auto"
          >
            <i className="ri-add-line"></i>
            发布新话题
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1 w-fit">
            {communityBoards.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setActiveBoard(item.key)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-full whitespace-nowrap transition-colors ${
                  activeBoard === item.key
                    ? 'bg-background-50 text-primary-700 font-medium'
                    : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                <i className={item.icon}></i>
                {item.name}
              </button>
            ))}
          </div>

          {activeBoard === 'following' ? (
            <div className="mt-4 flex items-center gap-2 rounded-md bg-secondary-50 border border-secondary-200 px-4 py-3 text-sm text-secondary-800">
              <i className="ri-information-line text-secondary-600"></i>
              「我的关注」为演示状态，实际关注关系数据待接入。
            </div>
          ) : null}

          <div className="mt-5 space-y-3">
            {visibleTopics.length > 0 ? (
              visibleTopics.map((topic) => (
                <button
                  key={topic.id}
                  type="button"
                  onClick={() => setSelectedTopic(topic)}
                  className="group w-full text-left flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 hover:border-primary-300 transition-colors"
                >
                  <span className="shrink-0 w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold">
                    {topic.avatar}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-sm md:text-base font-bold leading-snug text-foreground-950 line-clamp-2 group-hover:text-primary-700 transition-colors">
                      {topic.title}
                    </h2>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-400">
                      <span>{topic.author}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {topic.updatedAt}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-3-line"></i>
                        {topic.replies} 回复
                      </span>
                    </div>
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      {topic.tags.map((tag) => (
                        <span key={tag} className="rounded-full bg-background-100 px-2.5 py-0.5 text-xs text-foreground-500">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <i className="ri-arrow-right-s-line self-center text-foreground-300 group-hover:text-primary-500 transition-colors"></i>
                </button>
              ))
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 py-16 text-center">
                <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-chat-off-line text-2xl"></i>
                </span>
                <h2 className="text-base font-bold text-foreground-900">这里还比较安静</h2>
                <p className="mt-2 text-sm text-foreground-500">还不存在该板块的话题，换个板块看看吧。</p>
              </div>
            )}
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-24 space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-4">社区数据</h2>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="font-heading text-lg font-bold text-primary-600">{topicList.length}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">话题</p>
                </div>
                <div>
                  <p className="font-heading text-lg font-bold text-accent-600">
                    {topicList.reduce((sum, item) => sum + item.replies, 0)}
                  </p>
                  <p className="text-xs text-foreground-500 mt-0.5">回复</p>
                </div>
                <div>
                  <p className="font-heading text-lg font-bold text-secondary-600">9</p>
                  <p className="text-xs text-foreground-500 mt-0.5">常驻用户</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">社区公约</h2>
              <ul className="space-y-2.5 text-sm text-foreground-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-primary-500 mt-0.5"></i>
                  尊重不同观点，理性表达
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-primary-500 mt-0.5"></i>
                  分享真实经验，不传播未经证实的信息
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-primary-500 mt-0.5"></i>
                  共同维护友善、清爽的讨论氛围
                </li>
              </ul>
            </div>

            <div className="rounded-lg bg-accent-50 border border-accent-200 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">想分享你的见解？</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                从一个问题开始，也许就会遇到和你想法相近的人。
              </p>
              <button
                type="button"
                onClick={() => setNewOpen(true)}
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-accent-600 transition-colors"
              >
                <i className="ri-add-line"></i>
                发布新话题
              </button>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">读一篇好内容</h2>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-600 transition-colors"
              >
                返回首页浏览编辑精选
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </aside>
      </div>

      <button
        type="button"
        onClick={() => setNewOpen(true)}
        aria-label="发布新话题"
        className="lg:hidden fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors"
      >
        <i className="ri-add-line text-2xl"></i>
      </button>

      <NewTopicModal open={newOpen} onClose={() => setNewOpen(false)} onSubmit={handleSubmit} />
      <TopicDetailModal
        topic={selectedTopic}
        onClose={() => setSelectedTopic(null)}
        onToast={showToast}
      />

      {toast ? (
        <div className="fixed bottom-28 lg:bottom-10 left-1/2 -translate-x-1/2 z-[70] rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50 animate-soft-in">
          {toast}
        </div>
      ) : null}
    </div>
  );
}